import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
  alpha,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save,
  Business,
  Settings,
  Notifications,
  Payment,
  Security,
  Info,
  Check,
  CloudDone,
  Language,
} from '@mui/icons-material';

interface AppSettings {
  companyName: string;
  taxId: string;
  email: string;
  phone: string;
  address: string;
  currency: string;
  timezone: string;
  taxRate: number;
  sessionTimeout: number;
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  orderAlerts: boolean;
  cashEnabled: boolean;
  cardEnabled: boolean;
  paypalEnabled: boolean;
  paypalClientId: string;
  twoFactorAuth: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  companyName: 'Saheeh UG (haftungsbeschränkt)',
  taxId: '',
  email: '',
  phone: '',
  address: '',
  currency: 'EUR',
  timezone: 'Europe/Berlin',
  taxRate: 7,
  sessionTimeout: 30,
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  orderAlerts: true,
  cashEnabled: true,
  cardEnabled: true,
  paypalEnabled: false,
  paypalClientId: '',
  twoFactorAuth: false,
};

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [systemInfo, setSystemInfo] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('saheeh_language', lang);
  };

  // Load settings from Firestore
  const loadSettings = useCallback(async () => {
    setLoading(true);
    const { doc, getDoc, collection, getDocs } = await import('../../services/firestoreCompat');
    const { db } = await import('../../services/firebaseConfig');

    // Load settings
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'app_config'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const loaded: AppSettings = {
          companyName: data.companyName || DEFAULT_SETTINGS.companyName,
          taxId: data.taxId || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          currency: data.currency || 'EUR',
          timezone: data.timezone || 'Europe/Berlin',
          taxRate: data.taxRate ?? 7,
          sessionTimeout: data.sessionTimeout ?? 30,
          emailNotifications: data.emailNotifications ?? true,
          smsNotifications: data.smsNotifications ?? false,
          pushNotifications: data.pushNotifications ?? true,
          orderAlerts: data.orderAlerts ?? true,
          cashEnabled: data.cashEnabled ?? true,
          cardEnabled: data.cardEnabled ?? true,
          paypalEnabled: data.paypalEnabled ?? false,
          paypalClientId: data.paypalClientId || '',
          twoFactorAuth: data.twoFactorAuth ?? false,
        };
        setSettings(loaded);
        setOriginalSettings(loaded);
      }
    } catch (err) {
      console.warn('[SETTINGS] Failed to load settings:', err);
    }

    // Load system info independently
    try {
      const results = await Promise.allSettled([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'menu_items')),
        getDocs(collection(db, 'drivers')),
      ]);

      const getSize = (r: PromiseSettledResult<any>) => r.status === 'fulfilled' ? r.value.size : 0;

      setSystemInfo({
        totalOrders: getSize(results[0]),
        totalUsers: getSize(results[1]),
        totalMenuItems: getSize(results[2]),
        totalDrivers: getSize(results[3]),
        firebaseProject: 'saheeh',
        platform: 'Firebase + Firestore',
        hosting: 'Firebase Hosting',
        version: '2.0.0',
      });
    } catch (sysErr) {
      console.warn('[SETTINGS] System info failed:', sysErr);
      setSystemInfo({
        totalOrders: '-',
        totalUsers: '-',
        totalMenuItems: '-',
        totalDrivers: '-',
        firebaseProject: 'saheeh',
        platform: 'Firebase + Firestore',
        hosting: 'Firebase Hosting',
        version: '2.0.0',
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Track changes
  useEffect(() => {
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(originalSettings));
  }, [settings, originalSettings]);

  const update = (key: keyof AppSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Save settings to Firestore
  const handleSave = async () => {
    try {
      setSaving(true);
      const { doc, setDoc } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      await setDoc(doc(db, 'settings', 'app_config'), {
        ...settings,
        updated_at: new Date().toISOString(),
      }, { merge: true });

      // GoBD: persist a settings_change audit entry. Settings can change tax
      // rates, printer routing, and other report-affecting config — auditor
      // needs an immutable record of who changed what and when.
      try {
        const changedKeys: string[] = [];
        const beforeSnapshot: Record<string, any> = {};
        const afterSnapshot: Record<string, any> = {};
        for (const k of Object.keys(settings)) {
          const a = (settings as any)[k];
          const b = (originalSettings as any)[k];
          if (JSON.stringify(a) !== JSON.stringify(b)) {
            changedKeys.push(k);
            beforeSnapshot[k] = b ?? null;
            afterSnapshot[k] = a ?? null;
          }
        }
        if (changedKeys.length > 0) {
          const { httpsCallable, getFunctions } = await import('firebase/functions');
          const callable = httpsCallable(getFunctions(), 'logAuditEvent');
          await callable({
            action: 'settings_change',
            target: 'settings/app_config',
            before: beforeSnapshot,
            after: afterSnapshot,
            metadata: { changed_keys: changedKeys },
          });
        }
      } catch (auditErr) {
        console.warn('[AUDIT] settings_change emit failed (non-fatal):', auditErr);
      }

      setOriginalSettings({ ...settings });
      setSnackbar({ open: true, message: t('settings.settingsSaved'), severity: 'success' });
    } catch (err) {
      console.error('[SETTINGS] Save failed:', err);
      setSnackbar({ open: true, message: t('settings.settingsSaveFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...originalSettings });
    setResetDialogOpen(false);
  };

  // Section wrapper
  const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
    <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #3a3d52', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: '#9ca3af' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2.5, flex: 1 }}>{children}</Box>
    </Paper>
  );

  // Toggle row
  const ToggleRow = ({ label, description, checked, onChange }: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, borderBottom: '1px solid #f5f5f5', '&:last-child': { borderBottom: 'none' } }}>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>{label}</Typography>
        {description && <Typography variant="caption" sx={{ color: '#6b7280' }}>{description}</Typography>}
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2E7D32' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2E7D32' } }}
      />
    </Box>
  );

  // Compact text field
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& .MuiInputBase-input': { fontSize: '0.875rem' } };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Skeleton width={200} height={36} sx={{ mb: 3 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {[1, 2, 3, 4].map(i => <Skeleton key={i} height={280} sx={{ borderRadius: 2.5 }} />)}
          </Box>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
              {t('settings.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
              {t('settings.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {hasChanges && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setResetDialogOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#ddd', color: '#9ca3af' }}
              >
                {t('common.discard')}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? null : <Save sx={{ fontSize: 16 }} />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#e0e0e0',
                '&:hover': { bgcolor: '#e0e0e0' },
                '&:disabled': { bgcolor: '#e0e0e0', color: '#6b7280' },
              }}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2, '& .MuiAlert-icon': { color: '#1565C0' } }}>
            {t('settings.unsavedChanges')}
          </Alert>
        )}

        {/* Main Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          {/* Company Information */}
          <Section title={t('settings.companyInfo')} icon={<Business sx={{ fontSize: 18 }} />}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField fullWidth label={t('settings.companyName')} size="small" value={settings.companyName} onChange={e => update('companyName', e.target.value)} sx={inputSx} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField fullWidth label={t('settings.taxId')} size="small" value={settings.taxId} onChange={e => update('taxId', e.target.value)} sx={inputSx} placeholder="e.g. DE123456789" />
                <TextField fullWidth label={t('common.email')} size="small" value={settings.email} onChange={e => update('email', e.target.value)} sx={inputSx} placeholder="info@saheehpos.com" />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField fullWidth label={t('common.phone')} size="small" value={settings.phone} onChange={e => update('phone', e.target.value)} sx={inputSx} placeholder="+49 ..." />
                <TextField fullWidth label={t('common.address')} size="small" value={settings.address} onChange={e => update('address', e.target.value)} sx={inputSx} placeholder="Berlin, Germany" />
              </Box>
            </Box>
          </Section>

          {/* System Configuration */}
          <Section title={t('settings.systemConfig')} icon={<Settings sx={{ fontSize: 18 }} />}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Language Selector */}
              <FormControl fullWidth size="small">
                <InputLabel>{t('settings.language')}</InputLabel>
                <Select value={i18n.language?.startsWith('ar') ? 'ar' : 'en'} label={t('settings.language')} onChange={e => handleLanguageChange(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="en">{t('settings.english')}</MenuItem>
                  <MenuItem value="ar">{t('settings.arabic')}</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('settings.currency')}</InputLabel>
                  <Select value={settings.currency} label={t('settings.currency')} onChange={e => update('currency', e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="EUR">EUR (&euro;)</MenuItem>
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="GBP">GBP (&pound;)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('settings.timezone')}</InputLabel>
                  <Select value={settings.timezone} label={t('settings.timezone')} onChange={e => update('timezone', e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="Europe/Berlin">Europe/Berlin (CET)</MenuItem>
                    <MenuItem value="Europe/London">Europe/London (GMT)</MenuItem>
                    <MenuItem value="America/New_York">America/New York (EST)</MenuItem>
                    <MenuItem value="Asia/Tokyo">Asia/Tokyo (JST)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField fullWidth label={t('settings.taxRate')} size="small" type="number" value={settings.taxRate} onChange={e => update('taxRate', parseInt(e.target.value) || 0)} sx={inputSx} inputProps={{ min: 0, max: 100 }} />
                <TextField fullWidth label={t('settings.sessionTimeout')} size="small" type="number" value={settings.sessionTimeout} onChange={e => update('sessionTimeout', parseInt(e.target.value) || 15)} sx={inputSx} inputProps={{ min: 5, max: 480 }} />
              </Box>
            </Box>
          </Section>

          {/* Notifications */}
          <Section title={t('settings.notifications')} icon={<Notifications sx={{ fontSize: 18 }} />}>
            <ToggleRow label={t('settings.emailNotifications')} description={t('settings.emailNotificationsDesc')} checked={settings.emailNotifications} onChange={v => update('emailNotifications', v)} />
            <ToggleRow label={t('settings.smsNotifications')} description={t('settings.smsNotificationsDesc')} checked={settings.smsNotifications} onChange={v => update('smsNotifications', v)} />
            <ToggleRow label={t('settings.pushNotifications')} description={t('settings.pushNotificationsDesc')} checked={settings.pushNotifications} onChange={v => update('pushNotifications', v)} />
            <ToggleRow label={t('settings.orderAlerts')} description={t('settings.orderAlertsDesc')} checked={settings.orderAlerts} onChange={v => update('orderAlerts', v)} />
          </Section>

          {/* Payment Methods */}
          <Section title={t('settings.paymentMethods')} icon={<Payment sx={{ fontSize: 18 }} />}>
            <ToggleRow label={t('settings.cashPayments')} checked={settings.cashEnabled} onChange={v => update('cashEnabled', v)} />
            <ToggleRow label={t('settings.creditDebitCard')} checked={settings.cardEnabled} onChange={v => update('cardEnabled', v)} />
            <ToggleRow label="PayPal" checked={settings.paypalEnabled} onChange={v => update('paypalEnabled', v)} />
            {settings.paypalEnabled && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(0, 200, 150,0.08)', borderRadius: 2 }}>
                <TextField fullWidth label="PayPal Client ID" size="small" value={settings.paypalClientId} onChange={e => update('paypalClientId', e.target.value)} sx={inputSx} placeholder="Enter your PayPal client ID" />
              </Box>
            )}
          </Section>

          {/* Security */}
          <Section title={t('settings.security')} icon={<Security sx={{ fontSize: 18 }} />}>
            <ToggleRow label={t('settings.twoFactorAuth')} description={t('settings.twoFactorAuthDesc')} checked={settings.twoFactorAuth} onChange={v => update('twoFactorAuth', v)} />
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 200, 150,0.08)', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>{t('settings.passwordPolicy')}</Typography>
              {[t('settings.minChars'), t('settings.oneUppercase'), t('settings.oneNumber'), t('settings.oneSpecial')].map((r, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <Check sx={{ fontSize: 14, color: '#2E7D32' }} />
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>{r}</Typography>
                </Box>
              ))}
            </Box>
          </Section>

          {/* System Information */}
          <Section title={t('settings.systemInfo')} icon={<Info sx={{ fontSize: 18 }} />}>
            {systemInfo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { label: t('settings.version'), value: systemInfo.version },
                  { label: t('settings.platform'), value: systemInfo.platform, chip: true, color: '#2E7D32' },
                  { label: t('settings.hosting'), value: systemInfo.hosting },
                  { label: t('settings.firebaseProject'), value: systemInfo.firebaseProject, mono: true },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid #f5f5f5' }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>{item.label}</Typography>
                    {item.chip ? (
                      <Chip icon={<CloudDone sx={{ fontSize: '14px !important' }} />} label={item.value} size="small" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: alpha(item.color!, 0.1), color: item.color }} />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0', fontFamily: item.mono ? 'monospace' : 'inherit' }}>{item.value}</Typography>
                    )}
                  </Box>
                ))}
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(0, 200, 150,0.08)', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>{t('settings.databaseStats')}</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                    {[
                      { label: t('settings.ordersCount'), value: systemInfo.totalOrders },
                      { label: t('settings.staffCount'), value: systemInfo.totalUsers },
                      { label: t('settings.menuItemsCount'), value: systemInfo.totalMenuItems },
                      { label: t('settings.driversCount'), value: systemInfo.totalDrivers },
                    ].map((s, i) => (
                      <Box key={i} sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#e0e0e0' }}>{s.value}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>{s.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>{t('settings.loadingSystemInfo')}</Typography>
              </Box>
            )}
          </Section>
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Reset Dialog */}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 360 } }}>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('settings.discardChanges')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              {t('settings.discardConfirm')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 1.5, md: 3 }, pb: 2 }}>
            <Button onClick={() => setResetDialogOpen(false)} sx={{ textTransform: 'none', color: '#9ca3af' }}>{t('common.cancel')}</Button>
            <Button onClick={handleReset} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#d32f2f', '&:hover': { bgcolor: '#F44336' } }}>
              {t('common.discard')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default SettingsPage;
